<script lang="ts">
    import { _, date as formatDate } from 'svelte-i18n';
    import { time_update_minute as lowres_time} from './time';
    import { Activity } from "@/Common/ActivityFunctions";
    import ExerciseCard from './ExerciseCard/ExerciseCard.svelte';
    import type { MyFitnessStores } from './MyFitness/stores';
    import { type Writable, writable } from 'svelte/store';
    import { setContext } from 'svelte';
    import Select from 'svelte-select';
    import FilterModal from './FilterModal.svelte';
    import { getDifficultyFromDiamond, parseStorage, saveStorage, uniqueId, type XSSDetails } from './Util/utils';
    import RaceCard from './ExerciseCard/RaceCard.svelte';
    import type { RacePlan } from './Common/constants';
    import type { ProgramAchievability } from './Program/modules/ProgramAchievability';
    import { Button } from './lib/components/ui/button';

    // TODO (hyde): replace the whole UI with the new choose training UI
    
    export let dataSource: any = '';
    export let showOwner = false;
    export let myFitness: MyFitnessStores|null = null;
    export let isTrainingPicker = false;
    export let isCalendarPicker: boolean = false;
    export let isActivityPicker: boolean = false;
    export let isRacePicker: boolean = false;
    export let isRaceLibraryPicker: boolean = false;
    export let selected_path: string = null;
    export let dateTime: Date = null;
    export let forceFavOnly: boolean = false;
    export let excludeManual:string | null = null;
    export let excludeExercisesSet: Set<any> | null = null;
    export let perPage: number = 15;

    export let targetSignature: XSSDetails = null
    export let programAchievability: ProgramAchievability = null
    export let plan: Writable<RacePlan> = null
    export let mpaData = null

    const key = uniqueId('ActDash-')

    // ActivitiesDash is mounted two different ways: home.blade.php passes myFitness as a plain
    // prop, coach.blade.php passes it via the Svelte context Map instead. Re-set it as context here
    // whenever it arrives as a prop, so ExerciseCard (and its CommentsButton) can reliably read it
    // via getContext('myFitness') regardless of which mount site is in play.
    if (myFitness) setContext('myFitness', myFitness);

    // For bind only
    export let selected_exercise = null;

    $: selected_exercise = activities.data.find(activity=>activity.path == selected_path);
    let paginatorPages = [];

    let current_time = lowres_time;

    let requestLock = false;
    let currentPage = 1;
    let activitiesPromise: any;

    export let filterKey = 'activitiesDashFilter';
    let searchQuery: string = parseStorage(filterKey).searchBox ?? '';
    let searchBox: string = searchQuery;

    function emptyPaginator() {
        return {
            data: [],
            current_page: 1,
            last_page: 1
        }
    }
    let activities = emptyPaginator();

    let trickleActive: Writable<boolean> = myFitness?.trickleActive ?? writable(false);
    $: if (activities.current_page) paginatorPages = Array(7).fill(activities.current_page).map((x,i)=>x+i-3);
    $: isFirstPage = activities.current_page == 1 ? 'disabled' : '';
    $: isLastPage = activities.current_page == activities.last_page ? 'disabled' : '';

    async function _getActivities(page: number, search: string) {
        // console.trace(key, page, forceFavOnly, search, minDuration, maxDuration, minXSS, maxXSS, selectedFocus, selectedSpecificity, minDiamonds, maxDiamonds, showFavouritesOnly, showRecentsOnly)
        if(requestLock) {
            console.error('requestlock-'+key, page, forceFavOnly, search, minDuration, maxDuration, minXSS, maxXSS, selectedFocus, selectedSpecificity,selectedPill, minDiamonds, maxDiamonds, showFavouritesOnly, showRecentsOnly)
            return activitiesPromise;
        }
        requestLock = true;

        let endpoint: string = dataSource;
        
        endpoint += (showFavouritesOnly || forceFavOnly) ? '?searchFavourites=true' : '?searchFavourites=false';
        endpoint += '&page=' + page;
        if(search) endpoint += '&search=' + search;
        if(minDuration) endpoint += '&minDuration=' + minDuration;
        if(maxDuration) endpoint += '&maxDuration=' + maxDuration;
        if(minXSS) endpoint += '&minXSS=' + minXSS;
        if(maxXSS) endpoint += '&maxXSS=' + maxXSS;
        if(selectedFocus && selectedFocus.value != 0) endpoint += '&focus=' + selectedFocus.label;
        if(selectedSpecificity && selectedSpecificity.value != 0) endpoint += '&specificity=' + selectedSpecificity.label;
        if(minDiamonds) endpoint += '&minDifficulty=' + getDifficultyFromDiamond(minDiamonds.value);
        if(maxDiamonds) endpoint += '&maxDifficulty=' + getDifficultyFromDiamond(maxDiamonds.value);
        if(showRecentsOnly) endpoint += '&showRecentsOnly=true'; // TODO: recents don't actually do anything right now
        if(selectedSortBy && selectedSortOrder) endpoint += '&sortBy=' + selectedSortBy.value + '&sortOrder=' + selectedSortOrder.value;
        if(selectedPill) endpoint+= '&selectedPill=' + selectedPill.value;
        if(excludeManual) endpoint+= '&excludeManual=' + excludeManual;
        endpoint+='&perPage='+perPage;
        if (isRacePicker) endpoint += '&isRacePicker=true';
        if (showBrOnly) endpoint += '&showBrOnly=true';

        const response = await window.fetch(endpoint);
        let activities;
        if (response.ok) {
            activities = await response.json();
        } else {
            activities = emptyPaginator();
        }
        currentPage = activities.current_page;
        requestLock = false;
        return activities;
    }
    async function getActivities(page: number, search: string) {
        activitiesPromise = _getActivities(page, search)
        activities = await activitiesPromise;
        if (!isRacePicker) myFitness?.loadCommentCounts(activities.data.map((a) => a.path).filter(Boolean));
    }

    const allDefined = (...args) => args.every(arg => arg !== undefined)
    const doNothing = (...args) => true // Just a helper to let svelte watch the variables

    $: if (!$trickleActive && allDefined(showFavouritesOnly, selectedFocus, showRecentsOnly, searchQuery, selectedSortBy, selectedSortOrder, selectedSpecificity) && doNothing(minXSS, maxXSS, minDuration, maxDuration, minDiamonds, maxDiamonds)) {
        saveStorage(filterKey, 'showFavouritesOnly', showFavouritesOnly); // Save in case changed by toggle
        reloadActivities();
    }

    function deleteActivityCard(path) {
        let index = activities.data.findIndex((element) => element.path == path);
        activities.data.splice(index, 1);
        activities.data = activities.data;
    }

    function reloadActivities() {
        return getActivities(currentPage, searchQuery);
    }

    const calendarClearSelection = () => {
        selected_path = null;
    }

    let sortBy_common = [
        { label: 'Name', value: 1, },
        { label: 'Distance', value: 5, },
        { label: 'Elevation', value: 7, },
    ]
    let sortBy_isRaceLibPicker = [
        { label: 'Event Date', value: 6, },  
        ...sortBy_common,
    ]
    let sortBy = [
        { label: 'Date', value: 0, },
        ...sortBy_common,
        { label: 'Duration', value: 3, },
        { label: 'XSS', value: 2, },
        { label: 'Difficulty', value: 4, },
    ];
    let selectedSortBy = isRaceLibraryPicker ? sortBy_isRaceLibPicker[0] : sortBy[0]

    let sortOrder = [
        { label: 'Ascending', value: 0, },
        { label: 'Descending', value: 1, },
    ];
    export let defaultSort = 1;
    $: selectedSortOrder = sortOrder[defaultSort%sortOrder.length];

    let minDuration: number;
    let maxDuration: number;
    let minXSS: number;
    let maxXSS: number;
    let selectedFocus;
    let selectedSpecificity;
    let selectedPill;
    let minDiamonds;
    let maxDiamonds;
    let showFavouritesOnly;
    let showRecentsOnly;
    let showBrOnly = isRacePicker && !isRaceLibraryPicker;
    export let onSelect: (arg0?:any)=>any = ()=>{};

    const onSearchClick = () => {searchQuery = searchBox; saveStorage(filterKey, 'searchBox', searchBox);}

</script>

<dashboard>
    {#if !isRacePicker && isCalendarPicker && selected_exercise}
        <div class="searchbar box-solid border-none">
            <span class='box_header'>{`${$formatDate(dateTime, {day: '2-digit', month: 'short', year: 'numeric' })} - Activity Selected`}</span>
        </div>
        <ExerciseCard {isActivityPicker} {isTrainingPicker} {selected_path} exerciseType={"Activity"} showOwner={showOwner} {current_time} clearExercise={calendarClearSelection} showClearButton={true} showPlayButton={false} showScheduleButton={false} {deleteActivityCard} reloadExercises={reloadActivities} {...selected_exercise}/>
    {/if}

    <div class:mt-0={isRacePicker} class="topbar box-solid">
        <span class="searchbar">
            <input type=text on:keypress={(e) => {if(e.key == 'Enter') onSearchClick()}} bind:value={searchBox} placeholder="Search for an Activity"/>
            <button id="searchbtn" on:click={onSearchClick}>
                <i class="fa-solid fa-magnifying-glass"></i>
            </button>
        </span>
        <spacer></spacer>
        <span class="selector" id="sort-by">
            <p style="margin: auto !important; padding-right:5px; color: var(--color-xert-gray;">
                Sort By:
            </p>
            <div style="width: 160px;">
            <Select isClearable={false} bind:value={selectedSortBy} items={isRaceLibraryPicker ? sortBy_isRaceLibPicker : sortBy} showIndicator={true} indicatorSvg={'<i class="fa fa-angle-down" style="color: var(--color-xert-gray); top: 0px; height: 100%; line-height: var(--height);"/>'}/>
            </div>
        </span>
        <span class="selector" style="width: 140px;">
            <Select isClearable={false} bind:value={selectedSortOrder} items={sortOrder} showIndicator={true} indicatorSvg={'<i class="fa fa-angle-down" style="color: var(--color-xert-gray); top: 0px; height: 100%; line-height: var(--height);"/>'}/>
        </span>
        {#if !forceFavOnly && !isRacePicker}
            <div class="toggle-wrapper">
                <span class='mr-2'>Favourites only</span>
                <input type="checkbox" id={"fav-only-toggle-"+ key} bind:checked={showFavouritesOnly} />
                <label for={"fav-only-toggle-"+ key} />
            </div>
        {/if}
        <spacer style="flex-grow: 10;"></spacer>
        <FilterModal {filterKey} {forceFavOnly} hideFilterButton={isRaceLibraryPicker} showActivityFilters={true} defaultToBr={isRacePicker && !isRaceLibraryPicker} doLoadFromSessionStorage={true} onApply={()=>{searchBox = searchQuery}} title="Filter Workouts"
            bind:searchBox={searchQuery} bind:minDuration={minDuration} bind:maxDuration={maxDuration} bind:minXSS={minXSS} bind:maxXSS={maxXSS} bind:minDiamonds={minDiamonds} bind:maxDiamonds={maxDiamonds}
            bind:showFavouritesOnly bind:showRecentsOnly bind:showBrOnly bind:selectedFocus bind:selectedSpecificity bind:selectedPill
            />
    </div>

    <div class="cards mt-4"> 
        {#await activitiesPromise}
            <h3>Loading...</h3>
        {:then}
            {#each activities.data.filter(e=>!excludeExercisesSet?.has(e.path)) as activity (activity.path)}
                {#if !isRacePicker}
                    <ExerciseCard {isActivityPicker} {isTrainingPicker} selectable={isCalendarPicker || isActivityPicker}  {selected_path} exerciseType="Activity" showOwner={showOwner} {current_time} showPlayButton={false} showScheduleButton={false} {deleteActivityCard} reloadExercises={reloadActivities} {...activity} isPlanned={Activity.isPlanned(activity)} isRecorded={Activity.isRecorded(activity)} on:click={isCalendarPicker ? (_) => {selected_path = activity.path} : ((isActivityPicker)?(e)=>{selected_path = activity.path; onSelect(activity); }:null)}/>
                {:else}
                    <RaceCard on:select on:changeRace {isRaceLibraryPicker} {...activity} onAssess={() => onSelect(activity)} {plan} {mpaData} {targetSignature} {programAchievability} />
                {/if}
                <br/>
            {/each}
        {:catch error}
            <h3>Failed to load activities. Sorry!</h3>
            {error}
        {/await}
    </div>
    <div class="pagenator">
        <button class={isFirstPage} on:click={()=>{if(!isFirstPage) getActivities(1, searchQuery)}}><i class="fa-solid fa-angles-left"></i></button>
        <button class={isFirstPage} on:click={()=>{if(!isFirstPage) getActivities(activities.current_page-1, searchQuery)}}>Previous</button>
        {#each paginatorPages as page}
            {#if page >= 1 && page <= activities.last_page}
                <button class={page == activities.current_page ? 'selected' : ''} on:click={()=>{if(page != activities.current_page) getActivities(page, searchQuery)}}>{page}</button>
            {/if}
        {/each}
        <button class={isLastPage} on:click={()=>{if(!isLastPage) getActivities(activities.current_page+1, searchQuery)}}>Next</button>
        <button class={isLastPage} on:click={()=>{if(!isLastPage) getActivities(activities.last_page, searchQuery)}}><i class="fa-solid fa-angles-right"></i></button>
    </div>
</dashboard>

<style lang="scss">

@import './style/xert.scss';
    .topbar {
        @include medium-text;
        --fontSize: 16px;
        --height: 30px;
        --padding: 0 12px !important;
        --itemColor: var(--color-xert-gray-text);
        --border: 1px solid var(--color-xert-gray);
        --borderFocusColor: var(--color-xert-gray);
        --borderHoverColor: var(--color-xert-blue);
        --borderRadius: 3px !important;
        --listBorder: 1px solid var(--color-xert-blue);

        --indicatorRight: 20px;
        --indicatorTop: 0;
        --indicatorHeight: 0;
        --indicatorWidth: 0;

        display: flex;
        flex-wrap: wrap;
        justify-content: end;
        gap: 16px;
        padding: 15px 26px;
        border-radius: 4px;
        border: 1px solid #ECECEC;

        p {
            white-space: nowrap;
            margin: 0 !important;
        }

        .selector {
            display: inline-block;
        }

        > #sort-by{
            width: unset !important;
            display: flex;
        }

        a {
            color: #3D3D3D;

            > button, > button:focus {
                border-radius: var(--borderRadius) !important;
                background-color: white;
                border: var(--border) !important;
                height: 30px;
                padding: 6px 12px;
            }

            > button:hover, > button:active {
                border-color: var(--borderHoverColor) !important;
                background-color: white;
            }
        }

        > spacer {
            flex: 1 0 0;
        }
    }

    @media only screen and (max-width: 873px) and (min-width: 320px) { // rule somewhere else setting mb-[-10px] breaking things
        .box-solid {
            margin-bottom: 0;
        }
    }

    .searchbar {
        display: flex;
        flex: 10 1 200px;
        min-width: 200px;

        > input {
            min-width: 100px;
            flex-grow: 1;
            font-family: "Open Sans", sans-serif;
            font-size: 14px;
            height: 30px;
            padding: 2px 12px;
            border: 1px solid var(--color-xert-gray);
            border-top-left-radius: var(--borderRadius) !important;
            border-bottom-left-radius: var(--borderRadius) !important;
            border-right: none !important;
            outline: none;
            color: var(--color-xert-gray-text);
        }

        > button {
            height: 30px;
            width: 30px;
            border: 1px solid var(--color-xert-gray-text);
            border-top-right-radius: var(--borderRadius) !important;
            border-bottom-right-radius: var(--borderRadius) !important;
            border-left: none !important;
            background-color: white;

            > i {
                color: #ddd;
            }
        }

        > button:hover {
            > i {
                color: var(--color-xert-blue);
            }
        }
    }

    .show-liked-only, .show-disliked-only {
        margin: auto;
        white-space: nowrap;

        > input {
            vertical-align: middle;
            margin: 0px !important;
        }

        > p {
            margin: auto !important;
            color: var(--color-xert-gray);
            vertical-align: middle;
            display: inline-block;
            cursor: default;
            user-select: none;
        }
    }

    .searchbar #searchbtn:hover {
        > i {
            color: var(--color-xert-blue);
        }
    }

    .pagenator {
        display: flex;
        justify-content: center;
    }

    .pagenator button {
        padding: 8px 10px;
        font-size: 14px;
        line-height: 12px;

        border: 1px solid #ddd;
        color: var(--color-xert-blue);
        background-color: #fff;

        > i {
            color: var(--color-xert-blue);
        }
    }

    .pagenator button.disabled {
        border-color: #ddd !important;
        background-color: #fff !important;
        color: gray !important;
        cursor: not-allowed;

        > i {
            color: gray !important;
        }
    }

    .pagenator button.selected {
        border-color: #ddd !important;
        background-color: #eee !important;
        color: var(--xert-gray-text) !important;
        pointer-events: none;

        > i {
            color: var(--xert-gray-text) !important;
        }
    }

    .pagenator button:hover {
        border-color: #eee;
    }

    .pagenator button:active {
        border-color: var(--color-xert-blue);
        background-color: var(--color-xert-blue);
        color: white;

        > i {
            color: white;
        }
    }

</style>